/**
 * Reports API Service
 * Handles all report generation and export requests
 */

import { api_service } from './api';

export interface ReportParams {
  date_from?: string;
  date_to?: string;
  client_id?: string;
  format?: 'json' | 'csv' | 'pdf';
}

export interface ReportSummary {
  total_consumption?: number;
  total_revenue?: number;
  total_outstanding?: number;
  collection_rate?: number;
  [key: string]: any;
}

export interface ReportData {
  period?: {
    from: string;
    to: string;
  };
  summary: ReportSummary;
  [key: string]: any;
}

class ReportsService {
  private readonly BASE_PATH = '/reports';

  /**
   * Generate consumption report
   */
  async generateConsumptionReport(params: ReportParams): Promise<ReportData | Blob> {
    // For PDF/CSV, use blob response type
    if (params.format === 'pdf' || params.format === 'csv') {
      return await api_service.get<Blob>(`${this.BASE_PATH}/consumption/`, {
        params,
        responseType: 'blob',
      });
    }
    
    // For JSON, return data directly
    return await api_service.get<ReportData>(`${this.BASE_PATH}/consumption/`, { params });
  }

  /**
   * Generate revenue report
   */
  async generateRevenueReport(params: ReportParams): Promise<ReportData | Blob> {
    if (params.format === 'pdf' || params.format === 'csv') {
      return await api_service.get<Blob>(`${this.BASE_PATH}/revenue/`, {
        params,
        responseType: 'blob',
      });
    }
    
    return await api_service.get<ReportData>(`${this.BASE_PATH}/revenue/`, { params });
  }

  /**
   * Generate outstanding balances report
   */
  async generateOutstandingReport(params: ReportParams): Promise<ReportData | Blob> {
    if (params.format === 'pdf' || params.format === 'csv') {
      return await api_service.get<Blob>(`${this.BASE_PATH}/outstanding/`, {
        params,
        responseType: 'blob',
      });
    }
    
    return await api_service.get<ReportData>(`${this.BASE_PATH}/outstanding/`, { params });
  }

  /**
   * Generate client summary report
   */
  async generateClientSummary(client_id: string, format: 'json' | 'pdf' = 'json'): Promise<ReportData | Blob> {
    const params = { format };
    
    if (format === 'pdf') {
      return await api_service.get<Blob>(`${this.BASE_PATH}/client-summary/${client_id}/`, {
        params,
        responseType: 'blob',
      });
    }
    
    return await api_service.get<ReportData>(`${this.BASE_PATH}/client-summary/${client_id}/`, { params });
  }

  /**
   * Generate meter performance report
   */
  async generateMeterPerformanceReport(format: 'json' | 'pdf' = 'json'): Promise<ReportData | Blob> {
    const params = { format };
    
    if (format === 'pdf') {
      return await api_service.get<Blob>(`${this.BASE_PATH}/meter-performance/`, {
        params,
        responseType: 'blob',
      });
    }
    
    return await api_service.get<ReportData>(`${this.BASE_PATH}/meter-performance/`, { params });
  }

  /**
   * Generate collection analysis report
   */
  async generateCollectionAnalysis(params: ReportParams): Promise<ReportData | Blob> {
    if (params.format === 'pdf' || params.format === 'csv') {
      return await api_service.get<Blob>(`${this.BASE_PATH}/collection-analysis/`, {
        params,
        responseType: 'blob',
      });
    }
    
    return await api_service.get<ReportData>(`${this.BASE_PATH}/collection-analysis/`, { params });
  }

  /**
   * Download report file (PDF or CSV)
   * Creates a download link from a blob
   */
  downloadReport(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Generate and download report in one step
   * Uses api_service.download() method for convenience
   */
  async downloadReportDirect(
    endpoint: string, 
    filename: string, 
    params?: Record<string, any>
  ): Promise<void> {
    // Build full URL with query params if provided
    let url = `${this.BASE_PATH}${endpoint}`;
    if (params) {
      const queryString = new URLSearchParams(params as any).toString();
      url += `?${queryString}`;
    }
    
    await api_service.download(url, filename);
  }

  /**
   * Generate report with automatic retry on failure
   */
  async generateReportWithRetry(
    reportType: string,
    params: ReportParams,
    maxRetries: number = 2
  ): Promise<ReportData | Blob> {
    let lastError: any;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        switch (reportType) {
          case 'consumption':
            return await this.generateConsumptionReport(params);
          case 'revenue':
            return await this.generateRevenueReport(params);
          case 'outstanding':
            return await this.generateOutstandingReport(params);
          case 'collection-analysis':
            return await this.generateCollectionAnalysis(params);
          case 'meter-performance':
            // Meter performance only supports json and pdf, default csv to json
            const meterFormat = params.format === 'csv' ? 'json' : (params.format || 'json');
            return await this.generateMeterPerformanceReport(meterFormat);
          default:
            throw new Error(`Unknown report type: ${reportType}`);
        }
      } catch (error) {
        lastError = error;
        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
        }
      }
    }
    
    throw lastError;
  }
}

export const reportsService = new ReportsService();
export default reportsService;